import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  VStack,
  Input,
  Select
} from '@chakra-ui/react';
import ConfigChangeList from './ConfigChangeList';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { fetchConfigurationAudit } from '../../actions/configurations';
import { configurationUtils } from '@codametrix/ui-common';
const { serviceLineAliases } = configurationUtils;

interface ConfigurationHistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  tenantName: string;
}

const invertedServiceLineAliases: Record<string, string> = Object.fromEntries(
  Object.entries(serviceLineAliases ?? {}).map(([key, value]) => [value, key])
);

const ConfigurationHistoryDrawer: React.FC<ConfigurationHistoryDrawerProps> = ({
  isOpen,
  onClose,
  tenantId,
  tenantName
}) => {
  const [searchKey, setSearchKey] = useState('');
  const [submittedSearchKey, setSubmittedSearchKey] = useState('');
  const [serviceLine, setServiceLine] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();

  const activeContext = useSelector(
    (state: CMx.ApplicationState) => state.ui.context.activeContext
  );
  const { serviceLines = [] } = activeContext || {};

  // Get audit history from Redux store
  const configurationAuditData = useSelector(
    (state: CMx.ApplicationState) =>
      state.configurations.configurationAuditHistory
  );

  // Fetch audit history when drawer opens or filters change
  useEffect(() => {
    if (isOpen && tenantId) {
      const serviceLineForFetch =
        serviceLine === 'Bedside Pro'
          ? 'Inpatient Professional Coding'
          : serviceLine;

      dispatch(
        fetchConfigurationAudit({
          tenantId,
          pageNumber: pageNumber || 1,
          pageSize: 20,
          serviceLine: serviceLineForFetch || undefined,
          searchKey: submittedSearchKey || undefined
        })
      );
    }
  }, [isOpen, pageNumber, tenantId, serviceLine, submittedSearchKey, dispatch]);

  const handleServiceLineChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    setPageNumber(1);
    setServiceLine(selectedValue);
  };

  const handleSearchKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKey(e.target.value);
  };

  const handleSearchKeySubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setPageNumber(1);
      setSubmittedSearchKey(searchKey);
    }
  };

  const handlePageChange = (newPageNumber: number) => {
    if (newPageNumber !== pageNumber) {
      setPageNumber(newPageNumber);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      size="md"
      onClose={onClose}
      blockScrollOnMount={false}>
      <DrawerOverlay />
      <DrawerContent maxH="100vh" display="flex" flexDirection="column">
        <DrawerCloseButton />
        <DrawerHeader flexShrink={0} pr="5rem">
          Configuration Change History For {tenantName}
        </DrawerHeader>
        <DrawerBody
          flex="1"
          minH={0}
          overflow="hidden"
          display="flex"
          flexDirection="column"
          pt={2}>
          <VStack spacing={5} align="stretch" flexShrink={0}>
            <Input
              placeholder="Search"
              value={searchKey}
              onChange={handleSearchKeyChange}
              onKeyDown={handleSearchKeySubmit}
            />
            <Select
              placeholder="Service Line"
              value={serviceLine}
              onChange={handleServiceLineChange}>
              <option key="*" value="*">
                Tenant Default
              </option>
              {serviceLines.map(sl => (
                <option key={sl.name} value={sl.name}>
                  {invertedServiceLineAliases[sl.name] || sl.name}
                </option>
              ))}
            </Select>
          </VStack>
          <Box flex="1" minH={0} minW={0} display="flex" flexDirection="column" mt={5}>
            <ConfigChangeList
              changes={configurationAuditData.content}
              auditData={configurationAuditData}
              onPageChange={handlePageChange}
            />
          </Box>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default ConfigurationHistoryDrawer;
